import {Action, TabProps, Tabs} from 'platform/components';
import {Show} from 'platform/foundation';

import {GetSupplierOrderResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  queryParams,
  refreshDmsDatagrid,
  Section,
  usePermissions,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  Nullish,
  suffixTestId,
  TestIdProps,
  useBoolean,
  useQueryState,
} from 'shared/web';

import {GeneralInformationTab} from './components/GeneralInformationTab';
import {ListOfItemsTab} from './components/ListOfItemsTab';
import {MaterialModal} from './components/MaterialModal';

interface OverviewProps extends TestIdProps {
  supplierOrder: GetSupplierOrderResponse | Nullish;
  isPending: boolean;
  isOrderLoading: boolean;
  hasOrderError: boolean;
  isReadOnly: boolean;
}

export function Overview(props: OverviewProps) {
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const {supplierOrderId} = useWarehouseParams();
  const [isAddMaterialModalVisible, , , toggleAddMaterialModal] = useBoolean();

  const [supplierOrderEdit] = usePermissions({
    permissionKeys: ['supplierOrderEdit'],
  });

  const handleDiscardAddMaterialModal = () => {
    toggleAddMaterialModal();
    refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseSupplierOrderItems, {deselectAll: true});
  };

  const handleAddItem = () => {
    toggleAddMaterialModal();
  };

  const supplierListOfItemsActions = buildArray<Action>().when(props.isPending, {
    type: 'button',
    variant: 'outlined',
    title: i18n.t('entity.warehouse.labels.addMaterial'),
    onClick: handleAddItem,
    isDisabled: !supplierOrderEdit,
    tooltip: !supplierOrderEdit
      ? i18n.t('module.warehouse.label.missingSupplierOrderEditPermission')
      : undefined,
    'data-testid': suffixTestId('labels.addMaterial', props),
  });

  const tabs: TabProps[] = [
    {
      title: i18n.t('entity.warehouse.labels.generalInformation'),
      id: 'generalInformation',
      content: (
        <GeneralInformationTab
          supplierOrder={props.supplierOrder}
          isPending={props.isPending}
          isOrderLoading={props.isOrderLoading}
          hasOrderError={props.hasOrderError}
          data-testid={suffixTestId('generalInformation', props)}
        />
      ),
    },
    {
      title: i18n.t('entity.warehouse.labels.listOfItems'),
      id: 'listOfItems',
      actions: supplierListOfItemsActions,
      content: (
        <ListOfItemsTab
          isReadOnly={props.isReadOnly}
          totalPrice={props.supplierOrder?.totals}
          isPending={props.isPending}
          data-testid={suffixTestId('listOfItems', props)}
        />
      ),
    },
  ];

  return (
    <Section isFullHeight>
      <Tabs
        isFullHeight
        tabs={tabs}
        activeTabId={activeTabId}
        onChange={setActiveTabId}
        isLazy
        variant="condensed"
      />
      <Show when={isAddMaterialModalVisible}>
        <MaterialModal
          orderId={supplierOrderId}
          onDiscard={handleDiscardAddMaterialModal}
          data-testid={suffixTestId('dialogs.addMaterial', props)}
        />
      </Show>
    </Section>
  );
}
